import { useForm } from "@inertiajs/react"
import { type FormEvent, useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { listTasksPath } from "@/routes"
import type { List } from "@/types"

interface NewTaskModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  listId?: number
  lists?: List[]
  status?: string
}

export function NewTaskModal({
  open,
  onOpenChange,
  listId,
  lists,
  status,
}: NewTaskModalProps) {
  const [selectedListId, setSelectedListId] = useState<number | undefined>(
    listId,
  )

  const form = useForm({
    task: {
      description: "",
    },
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    const targetListId = lists && lists.length > 0 ? selectedListId : listId
    if (!targetListId) return
    form.post(listTasksPath(targetListId, { status }), {
      preserveScroll: true,
      onSuccess: () => {
        form.reset()
        onOpenChange(false)
      },
    })
  }

  const handleOpenChange = (isOpen: boolean) => {
    onOpenChange(isOpen)
    if (!isOpen) {
      form.reset()
      setSelectedListId(listId ?? lists?.[0]?.id)
    }
  }

  // When opening from dashboard, default to first list if no listId provided
  const showListSelector = lists && lists.length > 0

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>ADD TASK</DialogTitle>
            <DialogDescription>
              Add another soul to the list. The clock starts now.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {showListSelector && (
              <div className="space-y-2">
                <Label
                  htmlFor="list-select"
                  className="font-mono text-xs tracking-wider uppercase"
                >
                  List
                </Label>
                <Select
                  value={selectedListId?.toString() ?? ""}
                  onValueChange={(value) => setSelectedListId(Number(value))}
                >
                  <SelectTrigger id="list-select" className="w-full">
                    <SelectValue placeholder="Select a list" />
                  </SelectTrigger>
                  <SelectContent>
                    {lists.map((list) => (
                      <SelectItem key={list.id} value={list.id.toString()}>
                        {list.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label
                htmlFor="new-task"
                className={
                  showListSelector
                    ? "font-mono text-xs tracking-wider uppercase"
                    : "sr-only"
                }
              >
                Description
              </Label>
              <Input
                id="new-task"
                value={form.data.task.description}
                onChange={(e) =>
                  form.setData("task", {
                    ...form.data.task,
                    description: e.target.value,
                  })
                }
                placeholder="Enter task description..."
                autoComplete="off"
                autoFocus
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="submit"
              disabled={
                form.processing || (showListSelector && !selectedListId)
              }
            >
              Add Task
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
